import { INameIdFullName } from "./deltaHedgeBackTest";
import { DeltaHedgeOrderType } from "./deltaHedgeOrder";
import { OrderBook, OrderStatus } from "./orderInterfaces";
import { StepOptionOrderType, StepOrderSide } from "./stepInterfaces";
import { ISearchParameters, ITableReport } from "./TableInterfaces";
import { CoveredMatrixExecutionMethod } from "./coveredMatrixOrder";

// export enum CCOrderType {
//   Open = 1,
//   Offset = 2,
//   Roll = 3,
// }

export interface ICCOrderLeg {
  orderSide: StepOrderSide;
  instrumentId: string;
  optionOrderType?: StepOptionOrderType;
}

export enum CCOrderCriterionType {
  MinDailyReturn = 1,
  MinDailyRollReturn = 2,
  MaxRollCost = 3,
  MaxOffsetDailyReturn = 4,
  RollTimeDecayInvolveddFundRatioDaily = 5,
  DailyNetGainTimeRangeRollPercent = 6,
}

export enum CCOrderSendOrderType {
  Automatic = 0,
  Credit = 1,
  NonCredit = 2,
}

export enum CoveredCallSendPriorityMode {
  Random = 1,
  Manual = 2,
  FreeLiquidityAscending = 3,
  FreeLiquidityDescending = 4,
  CoveredCallPortfolioPercentAscending = 5,
  CoveredCallPortfolioPercentDescending = 6,
  BaseCoveredCallPortfolioPercentAscending = 7,
  BaseCoveredCallPortfolioPercentDescending = 8,
}

export interface ICoveredCallSendPriorityMode {
  id: number;
  name: string;
}

export interface ICoveredCallSendPriorityRow {
  accountId?: number | null;
  accountName: string;
  baseInstrumentId?: string | null;
  baseInstrumentName?: string | null;
  priority: number;
  metricValue?: number | null;
  isOther: boolean;
}

export interface ICoveredCallSendPriorityTableRow extends ITableReport {
  accountId?: number | null;
  accountName: string;
  baseInstrumentId?: string | null;
  baseInstrumentName: string;
  priority: number;
  metricValue?: number | null;
  metricDisplay: string;
  isOther: boolean;
}

export interface ICoveredCallSendPrioritySearchParams extends ISearchParameters {
  orderType: DeltaHedgeOrderType;
  mode?: CoveredCallSendPriorityMode;
  onlyUsersWithOrders: boolean;
}

export interface ICoveredCallSendPriorityView {
  orderType: DeltaHedgeOrderType;
  serverMode?: CoveredCallSendPriorityMode;
  mode: CoveredCallSendPriorityMode;
  canManage: boolean;
  otherPriority: number;
  updatedAt?: string | null;
  availableModes: ICoveredCallSendPriorityMode[];
  rows: ICoveredCallSendPriorityRow[];
}

export interface ICoveredCallSendPriorityAccount {
  id: number;
  name: string;
}

export interface ICoveredCallManualPriorityItem {
  accountId: number;
  priority: number;
}

export interface ICCOrderCriterionTypes {
  byProcess: {
    [id: number]: {
      id: number | string;
      name: string;
    }[];
  };
  defaults: {
    [id: number]: number;
  };
}

export interface ICCOrderImbalanceStage {
  waitSeconds: number | null;
  tolerancePercent: number | null;
}

export interface ICCOrderImbalanceRule {
  orderType: DeltaHedgeOrderType;
  criterionType: CCOrderCriterionType;
  stages: ICCOrderImbalanceStage[];
}

export interface ICCOrderImbalanceConfig {
  rows: ICCOrderImbalanceRule[];
  canManage: boolean;
}

export interface ICCOrderAddOrderFormValues {
  accessMessage?: string;
  originalVolumeCalculationType?: number;
  accountName?: string;
  userName?: string;
  instrumentNames?: string[];
  originalVolumeInput?: number | null;
  sheet?: string;
  row?: number;
  profileIds: number[];
  accountIds: number[];
  accountId: number; // for getOrder

  coveredCallOrderType: DeltaHedgeOrderType;
  coveredCallOrderBuyLegs: ICCOrderLeg[];
  coveredCallOrderSellLeg: ICCOrderLeg;
  totalVolume: number;
  criterion: number;
  criterionType?: number | null;
  sendOrderType: CCOrderSendOrderType;
  executionMethod?: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice?: number | null;
  baseInstrumentId?: string;
  blockSize: number;
  marginInPercent: number;
  coveredCallPositionStrategyEnum: any;
  volumeCalculationType: number;
  portfoPercent: number;
  previewWarnings?: string[];
  hasPreviewWarning?: boolean;
  canSend?: boolean;
}

export interface ICCOrderTableReport extends ITableReport {
  algorithm_order_id: number;
  condition_id: number;
  coveredCallOrderBuyLegs: ICCOrderLeg[];
  coveredCallOrderSellLeg: ICCOrderLeg;
  coveredCallOrderType: DeltaHedgeOrderType;
  isActive: boolean;
  orderStatus: OrderStatus;
  coveredCallPositionStrategyEnum: any;
  account: string;
  accountId: 0;
  process: string;
  instrumentName: string;
  instrumentIds: string[];
  orderSideStr: string;
  offsetOpenStatus: string;
  baseVolume: number;
  baseOrderedVolume: number;
  done: string;
  pending: string;
  exePrice: string;
  targetStr: string;
  target: number;
  criterionType?: number | null;
  criterionTypeStr?: string | null;
  sendOrderType?: CCOrderSendOrderType;
  sendOrderTypeStr?: string;
  creditAvailability?: number | null;
  nonCreditAvailability?: number | null;
  blockPercent: number;
  marginPercent: number;
  errorCount: number;
  dateStr: string;
  date: string;
  lastOrderStr: string;
  lastOrder: string;
}

// export interface ICCAggreageOrderTableReport extends ITableReport {
//   process: string;
//   coveredCallOrderType: DeltaHedgeOrderType;
//   instrumentName: string;
//   instrumentIds: string[];
//   orderSideStr: string;
//   orderSide1: StepOrderSide;
//   orderSide2: StepOrderSide;
//   offsetOpenStatus: string;
//   baseOrderedVolume: number;
//   done: string;
//   pending: string;
//   exePrice: string;
//   targetPercent: number;
//   errorCount: number;
//   dateStr: string;
//   lastChanceDateStr: string;
//   countOrder: number;
//   sameGroup: number;
//   date: string;
//   lastChanceDate: string;
//   orderStatus: OrderStatus;
//   isActive: boolean;
//   coveredCallOrderBuyLegs: ICCOrderLeg[];
//   coveredCallOrderSellLeg: ICCOrderLeg;
//   coveredCallPositionStrategyEnum: any;
// }

export interface ICCOrderSearchParams extends ISearchParameters {
  ids?: number[];
  coveredCallOrderType: DeltaHedgeOrderType;
  instrumentId: string;
  accountId: number;
  profileId: number;
  conditionId: number;
}

export interface ICCOrderbookSearchParams extends ISearchParameters {
  instrumentIds?: string[];
  orderSides?: StepOrderSide[];
  orderType?: DeltaHedgeOrderType;
  coveredCallPositionStrategyEnum?: any;
}

export interface ICCOrderbooksResp {
  orderBooks: OrderBook[];

  dailyRollGain: string | null;
  rollTimeDecayInvolveddFundRatioDaily: string | null;
  rollTimeDecayInvolveddFundRatioDailyNo: number | null;
  dailyNetGainTimeRangeRollPercent: string | null;
  dailyNetGainTimeRangeRollPercentNo: number | null;
  gainAfterCommissionDaily: string | null;
  gainAfterCommissionDailyOffset: string | null;
  pairPosition: number;
}

export interface IGetCCOrder {
  profileIds: number[];
  accountIds: number[];
  accountId: number;
  coveredCallOrderLegs: ICCOrderLeg[];
  coveredCallOrderSellLeg: ICCOrderLeg;
  orderType: DeltaHedgeOrderType;
  coveredCallPositionStrategyEnum: any;
  instrumentName: string[];
  blockSize: number;
  marginPercent: number;
  sendOrderType: CCOrderSendOrderType;
  executionMethod?: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice?: number | null;
  baseInstrumentId?: string;
  criterion: number;
  criterionType?: number | null;
  portfoPercent: number;
  involvedFund: number;
  volumeCalculationType: number;
  totalVolume: number;
}

export enum VolumeCalculationType {
  Static = 1,
  Portfo,
  Asset,
  PortfoTarget,
}

export interface ITextValue {
  value: number;
  text: string;
}

export interface ICCOrderRules {
  coveredCallRules: [
    {
      process: ITextValue;
      pairType: ITextValue;
      directions: ITextValue[];
      offsets: ITextValue[];
      criteria: string;
    },
  ];
  processeDropdowns: INameIdFullName[];
  pairTypeByProcessDropdown: {
    [id: number]: INameIdFullName[];
  };
  criteriaByProcessDropdown: {
    [id: number]: string;
  };
  directionsByPairType: {
    [id: number]: StepOrderSide[];
  };
  volumeCalculationTypesByProcess: {
    [id: number]: ITextValue[];
  };
}

export interface ICCOrderEditValues {
  applyToSimilar: boolean;
  id: number;
  conditionId: number;
  criterion: number;
  criterionType?: number | null;
}

export interface IUpdateCriterionDTO {
  coveredCallOrderLegs: ICCOrderLeg[];
  coveredCallOrderSellLeg: ICCOrderLeg;
  orderType: DeltaHedgeOrderType;
  coveredCallPositionStrategyEnum: number;
  newCriterion: number;
  criterionType?: number | null;
}

export interface ICCFileUploadResp extends ITableReport {
  orderRow: ICCFileUploadOrderRow;
  username: string;
  insNames: string;
  process: string;
  pairType: string;
  volume: string;
  target: string;
  block: string;
  margin: string;
}

export interface ICCFileUploadOrderRow extends ITableReport {
  accessMessage?: string;
  originalVolumeCalculationType?: number;
  accountName?: string;
  originalVolumeInput?: number | null;
  volumeCalculationType?: number;
  portfoPercent?: number;
  previewWarnings?: string[];
  canSend?: boolean;
  executionMethod?: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice?: number | null;
  accountId: number;
  accountIds: number[];
  coveredCallOrderTypeEnum: any;
  coveredCallOrderType: number;
  coveredCallPositionStrategyEnum: number;
  coveredCallOrderBuyLegs: ICCOrderLeg[];
  coveredCallOrderSellLeg: ICCOrderLeg;
  totalVolume: number;
  criterion: number;
  criterionType?: number | null;
  blockSize: number;
  marginInPercent: number;
  sendOrderType: CCOrderSendOrderType;
  row: number;
  userName: string;
  sheet: string;
  instrumentNames: string[];
}
